import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Checkbox, FormControlLabel, Stack, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ReactComponent as CheckedIcon } from 'src/assets/icons/checked-icon.svg';
import { ReactComponent as LogoIcon } from 'src/assets/icons/logo-icon.svg';
import { ReactComponent as UncheckedIcon } from 'src/assets/icons/unchecked-icon.svg';
import CConfirmModal from 'src/components/custom/modal/CConfirmModal';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { Page } from 'src/components/Page';
import { useStores } from 'src/models';
import { PATH_AUTH } from 'src/routes/paths';
import * as Yup from 'yup';

/**
 * ## 로그인
 *
 */

export interface ILoginData {
  loginId: string;
  pwd: string;
  isKeepLogin: boolean;
}

export const Login = observer(() => {
  const navigate = useNavigate();
  const { authStore } = useStores();
  const { state } = useLocation();
  const { t } = useTranslation();

  const [keepLogin, setKeepLogin] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const PageTitle = t('auth.login.title');

  const LoginSchema = Yup.object().shape({
    loginId: Yup.string().required(t('auth.login.enterId')).trim(),
    pwd: Yup.string().required(t('auth.login.enterPwd')),
  });

  const methods = useForm<{
    loginId: string;
    pwd: string;
  }>({
    resolver: yupResolver(LoginSchema),
    defaultValues: { loginId: state?.findId ? state?.findId : '', pwd: '' },
  });

  const { getValues, handleSubmit } = methods;

  const callApiLogin = async (isDisconnectAll?: boolean) => {
    const loginData = {
      loginId: getValues('loginId'),
      pwd: getValues('pwd'),
      isDisconnectAll: isDisconnectAll ? isDisconnectAll : false,
      isKeepLogin: keepLogin,
    } as ILoginData;

    const result = await authStore.login(loginData);
    if (result?.resultCode === 'S') {
      await authStore.getUserProfile();
    } else {
      if (result?.errorCode === '409' && result.errorMessage) {
        setErrorMsg(result.errorMessage);
      }
    }
  };

  const onSubmit = async () => {
    callApiLogin();
  };

  return (
    <MainContainer title={PageTitle} needPadding={false}>
      <SubContainer>
        <LogoContainer>
          <LogoIcon className="logo" />
        </LogoContainer>

        <FormContainer methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Stack sx={{ gap: '40px' }}>
            <Stack sx={{ gap: '24px' }}>
              <RHFTextField variant="standard" name="loginId" placeholder={t('common.loginId')} />
              <RHFTextField
                variant="standard"
                name="pwd"
                type="password"
                placeholder={t('common.pwd')}
              />
            </Stack>

            <KeepLoginButton
              control={
                <Checkbox
                  checked={keepLogin}
                  icon={<UncheckedIcon />}
                  checkedIcon={<CheckedIcon />}
                  onChange={() => {
                    setKeepLogin(!keepLogin);
                  }}
                />
              }
              label={t('auth.login.keepLogin')}
            />

            <LoginButton type="submit" variant="contained">
              {t('auth.login.login')}
            </LoginButton>
          </Stack>

          <LoginHelpContainer>
            <LoginHelpButton
              variant="text"
              onClick={() => {
                navigate(PATH_AUTH.findId.root);
              }}
            >
              {t('auth.findId.title')}
            </LoginHelpButton>
            <LoginHelpIcon icon={'fluent:divider-short-16-regular'} />
            <LoginHelpButton
              variant="text"
              onClick={() => {
                navigate(PATH_AUTH.resetPwd.root);
              }}
            >
              {t('auth.resetPwd.title')}
            </LoginHelpButton>
            <LoginHelpIcon icon={'fluent:divider-short-16-regular'} />
            <LoginHelpButton
              variant="text"
              onClick={() => {
                navigate(PATH_AUTH.register.root);
              }}
            >
              {t('auth.register.title')}
            </LoginHelpButton>
          </LoginHelpContainer>
        </FormContainer>
      </SubContainer>

      {errorMsg && (
        <CConfirmModal
          isOpen={!!errorMsg}
          title={errorMsg}
          confirmText={t('common.yes')}
          onConfirm={() => {
            callApiLogin(true);
          }}
          cancelText={t('common.no')}
          onCancel={() => {
            setErrorMsg(null);
          }}
        />
      )}
    </MainContainer>
  );
});

export default Login;

const MainContainer = styled(Page)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
}));

const SubContainer = styled(Stack)(() => ({
  width: '640px',
  height: '660px',
  gap: '56px',
  alignItems: 'center',
  margin: 'auto',
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  '& svg path': {
    stroke: theme.palette.common.white,
  },
  '.logo path': {
    stroke: 'none',
    fill: theme.palette.common.white,
  },
}));

const FormContainer = styled(FormProvider)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: '20px',
  padding: '40px 80px',
  gap: '40px',
  backgroundColor: theme.palette.common.white,
}));

const KeepLoginButton = styled(FormControlLabel)(() => ({
  '& .MuiFormControlLabel-label': {
    lineHeight: '21px',
  },
}));

const LoginButton = styled(Button)(() => ({
  height: '60px',
  borderRadius: '8px',
  fontSize: '18px',
  lineHeight: '150%',
}));

const LoginHelpContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
}));

const LoginHelpIcon = styled(Iconify)(({ theme }) => ({
  color: theme.palette.grey[300],
}));

const LoginHelpButton = styled(Button)(({ theme }) => ({
  padding: 0,
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  color: theme.palette.common.black,
}));
